import { IStyle, ITextFieldProps, Label, MessageBarType } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesStealth,
  CodeBlock,
  IH2OTheme,
  Info,
  KeyValuePairEditor,
  Loader,
  loaderStylesSpinnerButtonPrimary,
  TextField,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import { useMutation, useQuery } from '@tanstack/react-query';
import React from 'react';

import {
  AuthenticationPassphrase_HashAlgorithm,
  AuthorizationAccessToken_AuthorizationProtocolSecurityType,
} from '../../mlops/gen/ai/h2o/mlops/deployer/v2/security_pb';
import type { SecurityKey } from '../../mlops/utils';
import { getAccessToken } from '../../services/api';
import { useDebouncedCallback } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatError } from '../../utils/utils';
import { useConfirmDialog } from './ConfirmDialogProvider';
import { useGranularMLOpsPermissions } from './userActions';

export interface IDeploymentTabScoringStyles {
  form: IStyle;
  loader: IStyle;
  customEndpointButton: IStyle;
  infoLabel: IStyle;
  tooltip: IStyle;
  loaderWrapper: IStyle;
}

export const deploymentTabScoringStyles = (theme: IH2OTheme): Partial<IDeploymentTabScoringStyles> => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 20,
    margin: '0px 10px',
    // TODO: Remove once theme is used.
    borderColor: theme.semanticColors?.bodyBackground,
  },
  loader: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  customEndpointButton: {
    margin: '6px 0',
  },
  infoLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  tooltip: {
    padding: 4,
  },
  loaderWrapper: { height: 360, display: 'flex', justifyContent: 'center', alignItems: 'center' },
});

export interface IDeploymentTabScoringProps {
  defaultSelectedSecurityOptionKey?: SecurityKey;
  hasNoReplicas: boolean; // Used to show a warning if no replicas are set.
  isNew: boolean;
  plainTextPassphrase?: string;
  sampleRequestUrl?: string;
  scorerEndpoint?: string;
  workspaceId: string;
}

const scoringConfigToJSON = (scoringConfig: { [key: string]: string }) =>
    JSON.stringify({
      fields: Object.keys(scoringConfig),
      rows: [Object.values(scoringConfig)],
    }),
  sampleDataToScoringConfig = (sampleData: { fields: string[]; rows: string[] }) => {
    if (
      !sampleData ||
      !sampleData.fields ||
      !sampleData.rows ||
      sampleData.fields.length === 0 ||
      sampleData.rows.length === 0
    ) {
      return {};
    }
    return sampleData.fields.reduce((acc: { [key: string]: string }, field: string, index: number) => {
      acc[field] = sampleData.rows[0][index];
      return acc;
    }, {});
  };

export const DeploymentTabScoring = ({
  defaultSelectedSecurityOptionKey,
  scorerEndpoint,
  sampleRequestUrl,
  plainTextPassphrase,
  isNew,
  hasNoReplicas,
  workspaceId,
}: IDeploymentTabScoringProps) => {
  const theme = useTheme(),
    classNames = useClassNames<IDeploymentTabScoringStyles, ClassNamesFromIStyles<IDeploymentTabScoringStyles>>(
      'deploymentTabScoringStyles',
      deploymentTabScoringStyles(theme)
    ),
    { addToast } = useToast(),
    { showDialog } = useConfirmDialog(),
    workspaceName = React.useMemo(() => `workspaces/${workspaceId}`, [workspaceId]),
    permissions = useGranularMLOpsPermissions(workspaceName),
    isSecured = defaultSelectedSecurityOptionKey !== 'NO_SECURITY',
    [scoringConfig, setScoringConfig] = React.useState<{ [key: string]: string }>({ Field: 'column' }),
    [hashedPassphrase, setHashedPassphrase] = React.useState<string>(``),
    setHashedPassphraseDebounced = useDebouncedCallback((newValue) => setHashedPassphrase(newValue), 300),
    keyValuePairEditorStyles = React.useMemo(
      () => ({
        root: {
          backgroundColor: theme.semanticColors?.contentBackground,
          maxWidth: 460,
          border: `1px solid ${theme.semanticColors?.inputBorder}`,
          borderRadius: 4,
        },
      }),
      [theme.semanticColors?.contentBackground, theme.semanticColors?.inputBorder]
    );

  const securityHashedPassphrase =
    defaultSelectedSecurityOptionKey === AuthenticationPassphrase_HashAlgorithm.BCRYPT ||
    defaultSelectedSecurityOptionKey === AuthenticationPassphrase_HashAlgorithm.PBKDF2;
  const securityPassphrase =
    securityHashedPassphrase || defaultSelectedSecurityOptionKey === AuthenticationPassphrase_HashAlgorithm.PLAINTEXT;
  const scoringPassphrase =
    securityPassphrase &&
    (defaultSelectedSecurityOptionKey === AuthenticationPassphrase_HashAlgorithm.PLAINTEXT
      ? plainTextPassphrase
      : hashedPassphrase);

  const sampleRequestQuery = useQuery({
    enabled: Boolean(
      permissions?.isAbleToQuickScoreDeployment &&
        !hasNoReplicas &&
        sampleRequestUrl &&
        defaultSelectedSecurityOptionKey &&
        (!securityPassphrase || scoringPassphrase)
    ),
    meta: {
      toastError: (err: unknown) => `Failed to get sample request: ${formatError(err)}`,
    },
    queryFn: async ({ signal }) => {
      const bearerToken =
        defaultSelectedSecurityOptionKey === 'NO_SECURITY'
          ? undefined
          : defaultSelectedSecurityOptionKey === AuthorizationAccessToken_AuthorizationProtocolSecurityType.OIDC
          ? getAccessToken()
          : scoringPassphrase;
      const response = await fetch(sampleRequestUrl!, {
        headers: {
          Accept: 'application/json',
          ...(bearerToken && {
            Authorization: `Bearer ${bearerToken}`,
          }),
        },
        method: `GET`,
        signal,
      });
      if (!response.ok) throw await response.text();
      return response.json() as Promise<{ fields: string[]; rows: string[] }>;
    },
    queryKey: [
      `mlops`,
      `quickScoring`,
      `sampleRequest`,
      {
        sampleRequestUrl,
        // we need to add the security to the query key, so change of the security model or passphrase will refetch the query
        security: securityPassphrase ? `passphrase:${scoringPassphrase}` : defaultSelectedSecurityOptionKey,
      },
    ],
    select: sampleDataToScoringConfig,
  });
  const isPassphraseSet = securityPassphrase && sampleRequestQuery.isSuccess;
  const passphraseError =
    securityPassphrase && sampleRequestQuery.error instanceof Response && sampleRequestQuery.error.status === 401
      ? 'Invalid passphrase'
      : undefined;
  const sampleScoringConfig = sampleRequestQuery.data;
  React.useEffect(() => {
    if (sampleScoringConfig) {
      setScoringConfig((oldConfig) => {
        return Object.keys(sampleScoringConfig).reduce((acc, key) => {
          acc[key] = oldConfig[key] || ``;
          return acc;
        }, {} as Record<string, string>);
      });
    }
  }, [sampleScoringConfig]);
  const isConfigInitial = React.useMemo(
    () => !sampleScoringConfig || !Object.values(scoringConfig).some(Boolean),
    [scoringConfig, sampleScoringConfig]
  );
  const isConfigChanged = React.useMemo(
    () => JSON.stringify(scoringConfig) !== JSON.stringify(sampleScoringConfig),
    [scoringConfig, sampleScoringConfig]
  );

  const scoreMutation = useMutation({
    onError: (err) => {
      const message = `Failed to score: ${formatError(err)}`;
      console.error(message);
      addToast({
        messageBarType: MessageBarType.error,
        message,
      });
    },
    onSuccess: () => {
      addToast({
        messageBarType: MessageBarType.success,
        message: 'Scoring successfull.',
      });
    },
    mutationFn: async (scoringConfig: Record<string, string>) => {
      if (!scorerEndpoint) throw new Error(`Scorer endpoint is not defined`);
      const bearerToken =
        defaultSelectedSecurityOptionKey === 'NO_SECURITY'
          ? undefined
          : defaultSelectedSecurityOptionKey === AuthorizationAccessToken_AuthorizationProtocolSecurityType.OIDC
          ? getAccessToken()
          : scoringPassphrase;
      const response = await fetch(scorerEndpoint, {
        body: scoringConfigToJSON(scoringConfig),
        headers: {
          ...(bearerToken && {
            Authorization: `Bearer ${bearerToken}`,
          }),
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });
      if (!response.ok) throw response;
      return response.text();
    },
  });
  const isScoring = scoreMutation.isLoading;
  const scoringResponse = scoreMutation.data;

  return (
    <div className={classNames.form}>
      {hasNoReplicas ? (
        <Info style={{ marginTop: 10 }} styles={{ text: { alignContent: 'center', fontSize: 12 } }}>
          This deployment has no replicas set. Set replicas to a value greater than 0 to enable scoring.
        </Info>
      ) : permissions?.isAbleToQuickScoreDeployment ? (
        <>
          {!isNew && securityHashedPassphrase && !isPassphraseSet ? (
            <>
              <TextField
                label="Passphrase"
                onChange={(_ev, value) => {
                  setHashedPassphraseDebounced(value || '');
                  if (!value) {
                    // when user clears the input, propagate the change immediately - if there is some request in fly
                    // this will change the query key and cancel the request via AbortSignal, avoiding toast
                    // notification and safe some resources
                    setHashedPassphraseDebounced.flush();
                  }
                }}
                styles={{ root: { maxWidth: 460 } }}
                errorMessage={passphraseError}
                onRenderLabel={(props?: ITextFieldProps) => (
                  <div id={'load-hash'} className={classNames.infoLabel}>
                    {props?.label}
                    {sampleRequestQuery.isFetching ? <Loader styles={{ root: { marginLeft: 4 } }} size={0} /> : null}
                  </div>
                )}
              />
              <Info styles={{ text: { alignContent: 'center' } }}>
                This deployment is hashed. Enter the passphrase to enable all quick scoring features.
              </Info>
              <div style={{ height: 15 }} />
            </>
          ) : null}
          {defaultSelectedSecurityOptionKey === 'PLAINTEXT' ||
          defaultSelectedSecurityOptionKey === 'NO_SECURITY' ||
          defaultSelectedSecurityOptionKey === AuthorizationAccessToken_AuthorizationProtocolSecurityType.OIDC ||
          isPassphraseSet ? (
            <>
              <Label>Scoring configuration</Label>
              {!sampleRequestUrl ? (
                <Info
                  styles={{
                    text: {
                      alignContent: 'center',
                      color: theme.semanticColors?.textSecondary,
                      marginTop: 4,
                      marginBottom: 6,
                    },
                  }}
                >
                  The sample request data was not provided for this deployment. You need to fill in the scoring config
                  manually.
                </Info>
              ) : sampleRequestQuery.isError ? (
                <Info
                  styles={{
                    text: {
                      alignContent: 'center',
                      color: theme.semanticColors?.inputErrorMessageText,
                      fontSize: 14,
                      marginBottom: 6,
                      marginTop: 4,
                      maxInlineSize: `60ch`,
                    },
                  }}
                >
                  Failed to load column names for this deployment. Some features may be unavailable. Try refreshing the
                  browser tab, and if the problem persists, contact our support team.
                </Info>
              ) : sampleRequestQuery.isLoading ? (
                <div className={classNames.loaderWrapper}>
                  <Loader label="Loading column names..." />
                </div>
              ) : (
                <>
                  <KeyValuePairEditor
                    config={scoringConfig}
                    onUpdateConfig={(newConfig) => {
                      setScoringConfig(newConfig);
                    }}
                    styles={keyValuePairEditorStyles}
                    keyLabel="Column name"
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', width: 460 }}>
                    <Button
                      className={classNames.customEndpointButton}
                      iconName="ReportAdd"
                      text="Populate with sample data"
                      disabled={!sampleScoringConfig || !isConfigChanged}
                      onClick={() => {
                        if (sampleScoringConfig) {
                          if (isConfigInitial) {
                            setScoringConfig(sampleScoringConfig);
                          } else {
                            showDialog?.({
                              title: 'Populate with sample data?',
                              description: 'Your changes made to the scoring config will be replaced with sample data.',
                              onConfirm: () => {
                                setScoringConfig(sampleScoringConfig);
                              },
                              confirmationButtonText: 'Populate',
                            });
                          }
                        }
                      }}
                      styles={buttonStylesStealth}
                    />
                  </div>
                </>
              )}
              <div id={'curl-scoring-request-label'} className={classNames.infoLabel}>
                cURL scoring request
                <Info isTooltip className={classNames.tooltip}>
                  Auto-generated based on the scoring configuration.
                </Info>
              </div>
              <CodeBlock showCopyButton>
                {`curl -X POST -H "Content-Type: application/json" -d '${scoringConfigToJSON(scoringConfig)}' ${
                  isSecured ? '-H "Authorization: Bearer ' : ''
                }${isSecured ? scoringPassphrase : ''}${isSecured ? '"' : ''} ${scorerEndpoint || '[SCORER_ENDPOINT]'}`}
              </CodeBlock>
              <div style={{ height: 25 }} />
              <div className={classNames.customEndpointButton} style={{ display: 'block' }}>
                <Button
                  text="Score"
                  onClick={() => {
                    scoreMutation.mutate(scoringConfig);
                  }}
                  styles={buttonStylesPrimary}
                  loading={isScoring}
                  loaderProps={{
                    // TODO: Center the loading spinner.
                    styles: loaderStylesSpinnerButtonPrimary,
                  }}
                />
              </div>
            </>
          ) : null}
          {scoringResponse && (
            <>
              <h3>Scoring results</h3>
              <CodeBlock
                styles={{
                  root: { borderRadius: 4 },
                  pre: { borderRadius: 4 },
                }}
              >
                {scoringResponse}
              </CodeBlock>
            </>
          )}
        </>
      ) : (
        <p>
          You have no permissions to quick score this deployment. Ask the workspace owner to grant you the necessary
          permissions.
        </p>
      )}
    </div>
  );
};
